"use client";

import React from "react";
import PurchasingControl from "@/components/product-card/product-card-purchasing-control";
import ProductCardSubtitle from "@/components/product-card/product-card-subtitle";
import {numberWithCommas} from "@/lib/helper-functions";
import {useAppSelector} from "@/redux/hooks";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import {useTheme} from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import {Skeleton} from "@mui/material";
import CommentStars from "@/components/utilities/comment-stars";
import FaIcon from "@/components/utilities/fa-icon";
import {PInfoSectionProps, Product} from "@/types/product";
import ProductCardTitle from "@/components/product-card/product-card-title";
import ProductLoginButton from "@/components/product-card/product-button-login";

function getDiscountedPrice(product: Product) {
    const finalPrice = (product.price * (100 - product.discount)) / 100;
    return numberWithCommas(finalPrice);
}

function InfoRow({icon, text}: { icon: string; text: string }) {
    const theme = useTheme();

    return (
        <Stack spacing={0.8} direction="row" alignItems="center">
            <FaIcon variant="regular" color={theme.palette.primary.main} faIcon={icon} />
            <Typography fontWeight={500} fontSize={14}>
                {text}
            </Typography>
        </Stack>
    );
}

function InfoItem({
                      label,
                      children,
                  }: {
    label: string;
    children: React.ReactNode;
}) {
    const theme = useTheme();

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            py={1.25}
            borderBottom= {`1px solid ${theme.palette.grey[100]}`}
        >
            <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                whiteSpace="nowrap"
            >
                {label}
            </Typography>

            <Box
                textAlign="left"
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                minWidth={0}
            >
                {children}
            </Box>
        </Box>
    );
}

export default function PInfoSection({product}: PInfoSectionProps) {
    const user = useAppSelector((state) => state.user.currentUser);
    const theme = useTheme();

    return (
        <Paper
            elevation={0}
            sx = {{
                width: "100%",
                p: 3,
                mb: 3,
                boxSizing: "border-box",
            }}
        >
            <Box width = "100%" textAlign = "center" >

            <Typography variant="h6" component="h1" mb={0.7}>
                    {product ? (
                        <ProductCardTitle
                            centerText
                            sx={{
                                fontSize: {xs: 18, md: 20},
                                fontWeight: 700,
                                lineHeight: 1.7,
                            }}
                            name={product?.namePersian || ""}
                            car={product?.carName || ""}
                        />
                    ) : (
                        <Skeleton variant="text" />
                    )}
                </Typography>

                <Typography color={theme.palette.grey[600]}>
                    {product ? (
                        <ProductCardSubtitle
                            sx={{
                                textAlign: "center",
                                fontSize: 14,
                                fontWeight: 400,
                            }}
                            company={product.companyName || ""}
                            techNumber={product.techNumber}
                        />
                    ) : (
                        <Skeleton variant="text" />
                    )}
                </Typography>
            </Box>

            <Box pt={1.5}>
                {user ? (
                    <>
                        <Box
                            mb={2}
                            px={1}
                            borderRadius={3}
                        >
                            <InfoItem label="بخش:">
                                <Typography fontWeight={500} fontSize={14}>
                                    {product?.sectionName || "-"}
                                </Typography>
                            </InfoItem>

                            <InfoItem label="امتیاز:">
                                <CommentStars stars={product?.stars ?? 0} />
                            </InfoItem>

                            <InfoItem label="وضعیت کالا:">
                                {product?.isExists ? (
                                    <InfoRow icon="far fa-truck" text="ثبت سفارش" />
                                ) : (
                                    <InfoRow icon="far fa-shopping-basket" text="ناموجود (امکان پیش‌خرید)" />
                                )}
                            </InfoItem>

                            <InfoItem label="آخرین ورودی:">
                                <InfoRow icon="far fa-clock" text={product?.lastBuyDate ?? "-"} />
                            </InfoItem>

                            <InfoItem label="قیمت:">
                                <Typography fontWeight={600} fontSize={17}>
                                    {numberWithCommas(product?.price ?? 0)}
                                    <Typography component="span" fontSize={13} ml={.5}>
                                        ریال
                                    </Typography>
                                </Typography>
                            </InfoItem>

                            <InfoItem label="تخفیف:">
                                <Typography
                                    fontWeight={600}
                                    fontSize={14}
                                    color={product?.discount ? "success.main" : "text.primary"}
                                >
                                    {product?.discount ? `${product.discount} درصد` : "-"}
                                </Typography>
                            </InfoItem>
                        </Box>

                        <Box
                            p={2}
                            borderRadius={2}
                            bgcolor={`${theme.palette.grey[50]}`}
                            textAlign="center"

                        >
                            {product?.isExists && (
                                <Box mb={1}>
                                    <Typography fontSize={13} color={theme.palette.grey[600]} mb={0.5}>
                                        مبلغ قابل پرداخت
                                    </Typography>

                                    <Typography
                                        fontWeight={700}
                                        fontSize={{md: 32, xs: 28}}
                                        color="primary"
                                    >
                                        {getDiscountedPrice(product)}
                                        <Typography component="span" fontSize={18} fontWeight={500} >
                                            ریال
                                        </Typography>
                                    </Typography>
                                </Box>
                            )}

                            {product?.isExists ? (
                                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                    <PurchasingControl
                                        isBigButton
                                        productName={`${product.namePersian} ${product.carName}`}
                                        id={product?.id?.toString() || ""}
                                    />
                                </Box>
                            ) : (
                                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                                    <Typography fontSize={13} color="text.secondary" mb={1.5}>
                                        این کالا موجود نیست؛ امکان ثبت پیش‌خرید فعال است.
                                    </Typography>

                                    <PurchasingControl
                                        preOrder
                                        isBigButton
                                        productName={`${product?.namePersian || ""} ${product?.carName || ""}`}
                                        id={product?.id?.toString() || ""}
                                    />
                                </Box>
                            )}
                        </Box>
                    </>
                ) : (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        textAlign="center"
                        bgcolor={`${theme.palette.grey[50]}`}
                        p={3}
                        borderRadius={4}
                    >
                        <Typography fontSize={14} fontWeight={500} color="primary" mb={2}>
                            برای مشاهده قیمت و اطلاعات کالا ابتدا وارد شوید
                        </Typography>
                        <ProductLoginButton />
                    </Box>
                )}
            </Box>
        </Paper>
    );
}